import React, { useState, useEffect, useCallback } from 'react';
import { Badge, Button, Modal, Select } from '@opendate-app/setlist';
import SalesDetailPage from './SalesDetailPage';
import {
  DICE_SYNC_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/diceSyncColumnsConfig';
import { compactCurrency } from '../utils/reportingUtils';
import { buildDiceSyncFilterGroups } from '../constants/diceSyncFiltersConfig';
import { toastSuccess, toastError, errorText } from '../../../shared/toastHelper';

const axios = require('axios').default;

const STATUS_COLORS = {
  on_calendar: 'success',
  unmatched_venue: 'error',
  cancelled: 'gray',
  not_approved: 'gray',
  not_on_calendar: 'warning',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
    params.venue_name = activeFilters.venue_name;
  }
  if (activeFilters.status && activeFilters.status.length > 0) {
    params.status = activeFilters.status;
  }
  return params;
};

const DICE_SYNC_CONFIG = {
  pageTitle: 'DICE Sync',
  pageSubtitle: 'Which DICE events reached your calendar, and the ticket counts DICE last reported.',
  sidebarKey: 'sales_dice_sync',
  defaultPreset: 'next_90_days',

  columns: DICE_SYNC_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingDiceSyncColumns_${teamId}`,
  firstColumnId: 'event_date',
  defaultSortRules: [{ field: 'event_date', direction: 'asc' }],

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/dashboard/sales/dice-sync/data.json`,
  detailResponseKey: 'dice_sync_rows',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/dashboard/sales/dice-sync/export`,
  salesExportSection: 'dice_sync',
  detailErrorMessage: 'Failed to load DICE sync data.',
  detailTableTitle: 'DICE Events',
  emptyMessage: 'No DICE events in this range.',
  searchPlaceholder: 'Search events or venues...',
  paginationLabel: 'events',
  itemLabel: (count) => `event${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current, prior) => [
    {
      label: 'On Calendar',
      value: (current.on_calendar || 0).toLocaleString(),
      current: current.on_calendar || 0,
      prior: prior.on_calendar || 0,
    },
    {
      label: 'Needs Attention',
      value: (current.needs_attention || 0).toLocaleString(),
      current: current.needs_attention || 0,
      prior: prior.needs_attention || 0,
    },
    {
      label: 'Tickets Sold',
      value: (current.tickets_sold || 0).toLocaleString(),
      current: current.tickets_sold || 0,
      prior: prior.tickets_sold || 0,
    },
    {
      label: 'Ticket Gross',
      value: compactCurrency(current.ticket_gross || 0),
      current: current.ticket_gross || 0,
      prior: prior.ticket_gross || 0,
    },
  ],
  kpiGridColumns: 4,

  chartColor: 'var(--sl-fg-brand, #1982C4)',
  overTimeKey: null,

  filterTitle: 'Filter DICE Events',
  filterSubtitle: 'Narrow by DICE venue or sync status.',
};

const UnmatchedVenueRow = ({ diceVenue, venueOwnershipOptions, onLink, onCreate, pending }) => {
  const [selected, setSelected] = useState('');

  return (
    <tr>
      <td>
        <div className="font-weight-bold">{diceVenue.name || 'Unnamed DICE venue'}</div>
        {diceVenue.address && <div className="small text-muted">{diceVenue.address}</div>}
      </td>
      <td className="text-nowrap">{diceVenue.event_count.toLocaleString()}</td>
      <td>
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
          <Select
            size="sm"
            width={220}
            searchable
            aria-label={`Opendate venue for ${diceVenue.name}`}
            placeholder="Choose a venue"
            options={venueOwnershipOptions.map(option => ({ value: String(option.id), label: option.name }))}
            value={selected}
            onChange={setSelected}
            disabled={pending}
          />
          <Button variant="secondary" size="sm" disabled={!selected || pending} onClick={() => onLink(diceVenue, selected)}>
            Link
          </Button>
          <span className="small text-muted">or</span>
          <Button variant="secondary" size="sm" disabled={pending || !diceVenue.name} onClick={() => onCreate(diceVenue)}>
            Add as new venue
          </Button>
        </div>
      </td>
    </tr>
  );
};

const DiceVenuesPanel = ({ filterOptions, pendingVenueId, onLink, onCreate }) => {
  const diceVenues = filterOptions.dice_venues || [];
  if (diceVenues.length === 0) return null;

  const unmatched = diceVenues.filter(venue => !venue.venue_ownership_id);
  const matched = diceVenues.filter(venue => venue.venue_ownership_id);

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h5 className="mb-1">DICE Venues</h5>
        <p className="small text-muted mb-3">
          DICE events only reach your calendar when their DICE venue is linked to one of your venues.
          {filterOptions.last_synced_at && ` Last synced ${new Date(filterOptions.last_synced_at).toLocaleString()}.`}
        </p>
        {filterOptions.last_sync_error && (
          <div className="alert alert-warning small" role="alert">{filterOptions.last_sync_error}</div>
        )}
        {unmatched.length > 0 && (
          <div className="table-responsive mb-3">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Not linked</th>
                  <th>Events</th>
                  <th>Link to</th>
                </tr>
              </thead>
              <tbody>
                {unmatched.map(diceVenue => (
                  <UnmatchedVenueRow
                    key={diceVenue.id}
                    diceVenue={diceVenue}
                    venueOwnershipOptions={filterOptions.venue_ownership_options || []}
                    onLink={onLink}
                    onCreate={onCreate}
                    pending={pendingVenueId === diceVenue.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {matched.length > 0 && (
          <ul className="list-unstyled small mb-0">
            {matched.map(diceVenue => (
              <li key={diceVenue.id}>
                {diceVenue.name} → <strong>{diceVenue.venue_ownership_name}</strong>
                <span className="text-muted">{` (${diceVenue.event_count.toLocaleString()} ${diceVenue.event_count === 1 ? 'event' : 'events'})`}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

function ReportingSalesDiceSyncPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});
  const [pendingVenueId, setPendingVenueId] = useState(null);
  const [venueToCreate, setVenueToCreate] = useState(null);

  const loadFilterOptions = useCallback(() => {
    if (!team?.id) return;
    axios.get(`/teams/${team.id}/reporting/dashboard/sales/dice-sync/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((error) => console.error('Failed to load DICE sync filter options', error));
  }, [team?.id]);

  useEffect(() => { loadFilterOptions(); }, [loadFilterOptions]);

  const afterVenueSaved = (diceVenue, res) => {
    toastSuccess(`${diceVenue.name} is now linked to ${res.data.venue_ownership_name}. Its events will appear after the DICE sync finishes, usually within a few minutes.`);
    loadFilterOptions();
  };

  const linkVenue = useCallback((diceVenue, venueOwnershipId) => {
    setPendingVenueId(diceVenue.id);
    axios.patch(`/teams/${team.id}/dice_venues/${diceVenue.id}.json`, { venue_ownership_id: venueOwnershipId })
      .then((res) => afterVenueSaved(diceVenue, res))
      .catch((error) => toastError(errorText(error, 'Could not link the DICE venue.')))
      .finally(() => setPendingVenueId(null));
  }, [team?.id]);

  const createVenue = useCallback(() => {
    const diceVenue = venueToCreate;
    setVenueToCreate(null);
    setPendingVenueId(diceVenue.id);
    axios.post(`/teams/${team.id}/dice_venues/${diceVenue.id}/venue_ownership.json`)
      .then((res) => afterVenueSaved(diceVenue, res))
      .catch((error) => toastError(errorText(error, 'Could not add the venue.')))
      .finally(() => setPendingVenueId(null));
  }, [team?.id, venueToCreate]);

  const configWithHandlers = {
    ...DICE_SYNC_CONFIG,
    buildFilterGroups: () => buildDiceSyncFilterGroups(filterOptions),
    renderNotice: () => (
      <>
        <DiceVenuesPanel
          filterOptions={filterOptions}
          pendingVenueId={pendingVenueId}
          onLink={linkVenue}
          onCreate={setVenueToCreate}
        />
        <Modal
          open={!!venueToCreate}
          onClose={() => setVenueToCreate(null)}
          type="warning"
          title={`Add ${venueToCreate?.name || 'this venue'} to your team?`}
          description="This creates a new venue on your team with one room, using the address DICE has on file. You can edit it under Settings → Venues."
          confirmLabel="Add venue"
          onConfirm={createVenue}
          cancelLabel="Cancel"
          onCancel={() => setVenueToCreate(null)}
        />
      </>
    ),
    renderCellValue: (col, row, { formatEventDate, formatEventDateTime, currencyFormatter: cf }) => {
      switch (col.id) {
        case 'event_date':
          return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '—'}</span>;
        case 'event_name':
          return row.calendar_event_id
            ? <a href={`/teams/${team.id}/confirms/${row.calendar_event_id}`}>{row.event_name || '—'}</a>
            : (row.event_name || '—');
        case 'dice_venue_name':
          return row.dice_venue_name || '—';
        case 'venue_name':
          return row.venue_name || '—';
        case 'dice_state':
          return row.dice_state || '—';
        case 'status':
          return (
            <Badge size="sm" color={STATUS_COLORS[row.status] || 'gray'}>
              {row.status_label}
            </Badge>
          );
        case 'tickets_sold':
          return (row.tickets_sold || 0).toLocaleString();
        case 'ticket_gross':
          return cf.format(row.ticket_gross || 0);
        case 'tickets_synced_at':
          return <span className="text-nowrap">{row.tickets_synced_at ? formatEventDateTime(row.tickets_synced_at) : '—'}</span>;
        default:
          return '—';
      }
    },
  };

  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithHandlers} />;
}

export default ReportingSalesDiceSyncPage;
